import { inject, Injectable } from '@angular/core';
import { HttpService } from './http-service';
import { FaqDto } from '../dto/FaqDto';
import { AuthService } from './auth-service';
import { SaveFamilyAnalyseTableDataRequest } from '../dto/SaveFamilyAnalyseTableDataRequest';
import { RetrieveAllFamiliesDataResponse } from '../dto/RetrieveAllFamiliesDataResponse';
import { RsvpData } from '../models/RsvpData';
import { SaveNewFamilyRequest } from '../dto/SaveNewFamilyRequest';
import { FaqCategory } from '../models/FaqCategory';

@Injectable({
  providedIn: 'root',
})
export class UpdateService {

  private httpService: HttpService = inject(HttpService);
  private authService: AuthService = inject(AuthService);

  private readonly updateSubPath = '/update';

  async uploadFile(csv: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', csv);
    this.authService.askToRefreshIfNeeded();

    return this.httpService.putFormData<any>(`${this.updateSubPath}/import-csv`, formData);
  }

  async saveFaqData(faqData: FaqCategory[]): Promise<FaqDto> {

    const faqDataToSend: FaqDto = { FAQ: faqData };
    this.authService.askToRefreshIfNeeded();

    return this.httpService.postToPromise<FaqDto>(`${this.updateSubPath}/faq`, JSON.stringify(faqDataToSend));
  }

  saveFamilyAnalyseTableData(familyId: string, newAttendingResponse: string, canRequireCoupleInvite: boolean, canRequireChildsInvite: boolean, staysForBrunch: boolean): Promise<RetrieveAllFamiliesDataResponse> {
    const updateDataToSend: SaveFamilyAnalyseTableDataRequest = {
      canRequireChildInvite: canRequireChildsInvite,
      canRequireCoupleInvite: canRequireCoupleInvite,
      staysForBrunch: staysForBrunch,
      attendingStatus: newAttendingResponse
    };
    this.authService.askToRefreshIfNeeded();

    return this.httpService.postToPromise<RetrieveAllFamiliesDataResponse>(`${this.updateSubPath}/${familyId}/update-table-analyse`, JSON.stringify(updateDataToSend));
  }

  createNewFamily(newFamilyUuid: string, rsvpData: RsvpData, privateData: { canInviteChildren: boolean; canInviteCouple: boolean; invitationType: string; departement: string}) {

    const person2 = rsvpData.person2 && rsvpData.person2?.firstName.length > 0 && rsvpData.person2?.lastName.length > 0 ? rsvpData.person2 : undefined;
    const childs = rsvpData.childs?.filter(
      child => child && child.firstName.length > 0 && child.lastName.length > 0
    );
    
    const dataToSend: SaveNewFamilyRequest = {
      canRequireChildInvite: privateData.canInviteChildren,
      canRequireCoupleInvite: privateData.canInviteCouple,
      invitationType: privateData.invitationType,
      staysForBrunch: rsvpData.staysForBrunch,
      attendingStatus: rsvpData.attendingStatus,
      person1: rsvpData.person1,
      familyUuid: newFamilyUuid,
      comment: rsvpData.comment,
      bonusQuestionAnswer: rsvpData.bonusQuestionAnswer,
      person2: person2,
      childs: childs,
      departement: privateData.departement
    };
    this.authService.askToRefreshIfNeeded();

    return this.httpService.postToPromise<boolean>(`${this.updateSubPath}/new-family`, JSON.stringify(dataToSend));
  }

  updateFamily(familyUuid: string, rsvpData: RsvpData, privateData: { canInviteChildren: boolean; canInviteCouple: boolean; invitationType: string; departement: string}) {

    const person2 = rsvpData.person2 && rsvpData.person2?.firstName.length > 0 && rsvpData.person2?.lastName.length > 0 ? rsvpData.person2 : undefined;
    const childs = rsvpData.childs?.filter(
      child => child && child.firstName.length > 0 && child.lastName.length > 0
    );
    
    const dataToUpdate: SaveNewFamilyRequest = {
      canRequireChildInvite: privateData.canInviteChildren,
      canRequireCoupleInvite: privateData.canInviteCouple,
      invitationType: privateData.invitationType,
      staysForBrunch: rsvpData.staysForBrunch,
      attendingStatus: rsvpData.attendingStatus,
      person1: rsvpData.person1,
      familyUuid: familyUuid,
      comment: rsvpData.comment,
      bonusQuestionAnswer: rsvpData.bonusQuestionAnswer,
      person2: person2,
      childs: childs,
      departement: privateData.departement
    };
    this.authService.askToRefreshIfNeeded();

    return this.httpService.put<boolean>(`${this.updateSubPath}/family`, JSON.stringify(dataToUpdate));
  }

  async uninviteFamily(familyUuid: string): Promise<boolean> {

    this.authService.askToRefreshIfNeeded();
    return this.httpService.delete<boolean>(`${this.updateSubPath}/${familyUuid}/uninvite`);
  }
}
